import type { JSX } from 'react';
import { describeApiError } from '../api';
import { RENDER_ERROR } from '../labels';

/**
 * A failed panel, said in Estonian: one sentence in the page's language, and the machine text
 * beside it as machine text rather than pasted into that sentence. `crashed` picks the wording for
 * a throw while drawing, which is not a request that failed. `role="status"` rather than `alert`:
 * a panel that could not load is worth announcing when the reader reaches it, not interrupting for.
 */

export interface ErrorNoteProps {
  error: unknown;
  /** What could not be loaded — the feed says "juhtumite", a chart says "andmete". */
  what?: string;
  /** A throw while drawing rather than a failed request: nothing was fetched, so the network
   * sentence and the "server said this" label on the detail would both be untrue. */
  crashed?: boolean;
}

/** A render throw carries a JavaScript message, not a field-level answer from the API. */
function crashDetail(error: unknown): string | null {
  return error instanceof Error && error.message !== '' ? error.message : null;
}

export function ErrorNote({
  error,
  what = 'Andmete',
  crashed = false,
}: ErrorNoteProps): JSX.Element {
  const failed = describeApiError(error);
  const lead = crashed ? RENDER_ERROR.lead(what) : `${what} laadimine ebaõnnestus.`;
  const text = crashed ? RENDER_ERROR.text : failed.text;
  const detail = crashed ? crashDetail(error) : failed.detail;
  const detailLabel = crashed ? RENDER_ERROR.detailLabel : 'Serveri vastus, masinatekst';

  return (
    <p className="chart-status chart-status--error" role="status">
      {lead} {text}
      {detail !== null && (
        <>
          {' '}
          <code className="error-detail" title={detailLabel}>
            {detail}
          </code>
        </>
      )}
    </p>
  );
}
